import { DateTime } from 'luxon'

import { Table } from '@components/Table'
import { ColumnType } from '@components/Table/interface'
import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { WholeStoryDiscountTypeEnum } from '@interfaces/promotionCampaign/WholeStoryDiscountTypeEnum'
import { FreeReadingTypeEnum } from '@interfaces/promotionCampaign/FreeReadingTypeEnum'
import { CampaignBookItemType } from '@models/promotionCampaign/CampaignBookItemType'
import { CampaignPreviewTableProps } from './interface'

const baseColumns: ColumnType<CampaignBookItemType>[] = [
  {
    column: 'Book ID',
    accessor: 'bookId',
    cell: ({ value }) => <p className='leading-[1.8]'>{value}</p>,
    cellClassName: 'p-[10px] min-w-[80px] max-w-[80px]',
  },
  {
    column: 'ชื่อหนังสือ',
    accessor: 'bookTitle',
    cell: ({ value }) => <p className='leading-[1.8] line-clamp-1'>{value}</p>,
    cellClassName: 'p-[10px] w-full',
  },
  {
    column: 'นามปากกา',
    accessor: 'bookPenFirstName',
    cell: ({ value }) => <p className='leading-[1.8] line-clamp-1'>{value}</p>,
    cellClassName: 'p-[10px] min-w-[120px] max-w-[120px]',
  },
  {
    column: 'สำนักพิมพ์',
    accessor: 'bookPublisherName',
    cell: ({ value }) => <p className='leading-[1.8] line-clamp-1'>{value}</p>,
    cellClassName: 'p-[10px] min-w-[120px] max-w-[120px]',
  },
]

function formatDateTime(value: string): string {
  return DateTime.fromISO(value, { zone: 'utc' })
    .toLocal()
    .toFormat('dd/MM/yyyy HH:mm:ss')
}

const dateColumns: ColumnType<CampaignBookItemType>[] = [
  {
    column: 'วันเริ่มต้น',
    accessor: 'startDate',
    cell: ({ value }) => (
      <p className='leading-[1.8]'>{value ? formatDateTime(value) : '-'}</p>
    ),
    cellClassName: 'p-[10px] min-w-[160px] max-w-[160px]',
  },
  {
    column: 'วันสิ้นสุด',
    accessor: 'endDate',
    cell: ({ value }) => (
      <p className='leading-[1.8]'>{value ? formatDateTime(value) : '-'}</p>
    ),
    cellClassName: 'p-[10px] min-w-[160px] max-w-[160px]',
  },
]

function getColumns(
  campaignType: CampaignTypeEnum,
  wholeStoryDiscountType?: WholeStoryDiscountTypeEnum,
  freeReadingType?: FreeReadingTypeEnum
): ColumnType<CampaignBookItemType>[] {
  if (campaignType === CampaignTypeEnum.PER_CHAPTER) {
    return [
      ...baseColumns,
      {
        column: 'ตั้งแต่ตอนที่',
        accessor: 'chapterIdStart',
        cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
        cellClassName: 'p-[10px] min-w-[100px] max-w-[100px]',
      },
      {
        column: 'ถึงตอนที่',
        accessor: 'chapterIdEnd',
        cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
        cellClassName: 'p-[10px] min-w-[80px] max-w-[80px]',
      },
      {
        column: 'ลดเหลือตอนละ',
        accessor: 'discountPricePerChapter',
        cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
        cellClassName: 'p-[10px] min-w-[110px] max-w-[110px]',
      },
      ...dateColumns,
    ]
  }

  if (campaignType === CampaignTypeEnum.WHOLE_STORY) {
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE) {
      return [
        ...baseColumns,
        {
          column: 'ราคาเต็ม',
          accessor: 'originalPrice',
          cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
          cellClassName: 'p-[10px] min-w-[90px] max-w-[90px]',
        },
        {
          column: 'ส่วนลด (เหรียญ)',
          accessor: 'discountCoin',
          cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
          cellClassName: 'p-[10px] min-w-[110px] max-w-[110px]',
        },
        {
          column: 'ราคาหลังลด',
          accessor: 'discountedPrice',
          cell: ({ value }) => <p className='leading-[1.8]'>{value || '-'}</p>,
          cellClassName: 'p-[10px] min-w-[100px] max-w-[100px]',
        },
        ...dateColumns,
      ]
    }

    // PERCENTAGE (default for WHOLE_STORY)
    return [
      ...baseColumns,
      {
        column: 'จำนวน % ที่ลดราคา',
        accessor: 'discountPercentage',
        cell: ({ value }) => (
          <p className='leading-[1.8]'>{value ? `${value}%` : '-'}</p>
        ),
        cellClassName: 'p-[10px] min-w-[130px] max-w-[130px]',
      },
      ...dateColumns,
    ]
  }

  // FREE_READING
  if (freeReadingType === FreeReadingTypeEnum.DAILY_LIMIT) {
    return [
      ...baseColumns,
      {
        column: 'อ่านฟรีวันละ',
        accessor: 'dailyChapters',
        cell: ({ value }) => (
          <p className='leading-[1.8]'>{value ? `${value} ตอน` : '-'}</p>
        ),
        cellClassName: 'p-[10px] min-w-[110px] max-w-[110px]',
      },
      ...dateColumns,
    ]
  }

  if (freeReadingType === FreeReadingTypeEnum.PERIOD_SPECIFIC) {
    return [
      ...baseColumns,
      {
        column: 'อ่านฟรีภายใน (วัน)',
        accessor: 'freeDays',
        cell: ({ value }) => (
          <p className='leading-[1.8]'>{value ? `${value} วัน` : '-'}</p>
        ),
        cellClassName: 'p-[10px] min-w-[130px] max-w-[130px]',
      },
      ...dateColumns,
    ]
  }

  // DURATION_SPECIFIC (default for FREE_READING)
  return [...baseColumns, ...dateColumns]
}

export function CampaignPreviewTable({
  data,
  campaignType,
  wholeStoryDiscountType,
  freeReadingType,
}: CampaignPreviewTableProps) {
  const columns = getColumns(
    campaignType,
    wholeStoryDiscountType,
    freeReadingType
  )

  return (
    <div className='mt-[20px]'>
      <p className='text-[16px] font-medium font-mitr text-secondary mb-[10px]'>
        รายการหนังสือ ({data.length} รายการ)
      </p>
      <Table data={data} columns={columns} />
    </div>
  )
}
